import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { LoadingScreen } from '@/components/LoadingScreen'
import { translate, useTranslation } from '@/i18n'
import {
  useInvitePreview,
  useJoinManagerLeagueFromInvite,
  useJoinManagerSubleagueInvite,
  useJoinPouleInvite,
  type InviteKind,
  type ManagerInvitePreview,
  type PouleInvitePreview,
} from '@/features/invites/api'

export function InvitePage({ kind = 'auto' }: { kind?: InviteKind }) {
  const { t } = useTranslation()
  const { token: routeToken } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const token = routeToken?.trim() || undefined
  const inviteQuery = useInvitePreview(token, kind)
  const preview = inviteQuery.data

  if (inviteQuery.isLoading) {
    return (
      <InviteShell>
        <LoadingScreen />
        <p className="mt-5 text-sm text-surface-400">{t('De invite wordt gecontroleerd…')}</p>
      </InviteShell>
    )
  }

  if (inviteQuery.isError || !preview) {
    return (
      <InviteShell>
        <InviteState
          eyebrow={t('Invite')}
          title={t('Deze invite-link werkt niet')}
          body={t('De link is ongeldig, verlopen of niet meer beschikbaar. Vraag de afzender om een nieuwe link.')}
          action={<Link to="/dashboard/poules" className={primaryButtonClass}>{t('Naar mijn poules')}</Link>}
        />
      </InviteShell>
    )
  }

  if (preview.kind === 'manager') {
    return (
      <ManagerInviteView
        token={token}
        preview={preview.data}
        onOpenLeague={(leagueId) => navigate(`/dashboard/manager/competities/${leagueId}`)}
        onBack={() => navigate('/dashboard/manager')}
        onReload={() => void inviteQuery.refetch()}
      />
    )
  }

  return (
    <PouleInviteView
      token={token}
      preview={preview.data}
      onOpenPoule={(pouleId) => navigate(`/poules/${pouleId}`)}
      onBack={() => navigate('/dashboard/poules')}
      onReload={() => void inviteQuery.refetch()}
    />
  )
}

function PouleInviteView({
  token,
  preview,
  onOpenPoule,
  onBack,
  onReload,
}: {
  token: string | undefined
  preview: PouleInvitePreview
  onOpenPoule: (pouleId: number) => void
  onBack: () => void
  onReload: () => void
}) {
  const { t } = useTranslation()
  const joinMutation = useJoinPouleInvite(token)
  const poule = preview.poule
  const membership = preview.membership_status_for_current_user
  const isMember = membership === 'accepted'
  const isPending = membership === 'pending'
  const canJoin = preview.invite_valid && !isMember && !isPending && Boolean(poule)

  async function handleJoin() {
    if (!canJoin) return
    try {
      const result = await joinMutation.mutateAsync()
      if (result.status === 'accepted' || result.status === 'already_member') {
        if (result.poule_id ?? poule?.id) onOpenPoule(result.poule_id ?? poule!.id)
        return
      }
      onReload()
    } catch {
      // The mutation message is rendered below; keep the preview in place.
    }
  }

  return (
    <InviteShell>
      <InviteBackLink onClick={onBack} label={t('Voetbalpoules')} />
      <InviteHeroImage src={poule?.hero_url ?? poule?.thumbnail_url} />
      <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-accent-300">{t('Voetbalpoule')}</p>
      <h1 className="mt-2 text-3xl font-black tracking-tight text-white">
        {poule?.name ?? t('Uitnodiging voor een poule')}
      </h1>
      <p className="mt-3 text-sm leading-6 text-surface-400">
        {poule?.description || t('Je bent uitgenodigd om samen wedstrijden te voorspellen en punten te verdienen.')}
      </p>
      <InviteMeta
        inviter={preview.inviter?.display_name || preview.inviter?.first_name}
        members={poule?.member_count}
        approval={poule?.requires_approval === true}
      />
      {!preview.invite_valid ? (
        <InviteState
          compact
          title={inviteReasonTitle(preview.reason)}
          body={t('Deze uitnodiging kan niet meer worden gebruikt.')}
        />
      ) : isMember ? (
        <InviteState
          compact
          title={t('Je zit al in deze poule')}
          body={t('Open de poule om direct verder te gaan met voorspellen.')}
          action={<button type="button" onClick={() => poule && onOpenPoule(poule.id)} className={primaryButtonClass}>{t('Open poule')}</button>}
        />
      ) : isPending ? (
        <InviteState compact title={t('Aanvraag verstuurd')} body={t('De beheerder moet je deelname nog goedkeuren.')} />
      ) : (
        <InviteAction
          label={poule?.requires_approval ? t('Aanvraag versturen') : t('Deelnemen aan poule')}
          pending={joinMutation.isPending}
          error={joinMutation.error?.message}
          onClick={() => void handleJoin()}
        />
      )}
    </InviteShell>
  )
}

function ManagerInviteView({
  token,
  preview,
  onOpenLeague,
  onBack,
  onReload,
}: {
  token: string | undefined
  preview: ManagerInvitePreview
  onOpenLeague: (leagueId: number) => void
  onBack: () => void
  onReload: () => void
}) {
  const { t } = useTranslation()
  const leagueId = preview.league?.id ?? preview.subleague?.fantasy_league_id
  const joinLeagueMutation = useJoinManagerLeagueFromInvite(token, leagueId)
  const joinSubleagueMutation = useJoinManagerSubleagueInvite(token)
  const nextAction = preview.next_action
  const isJoined = nextAction === 'already_joined'
  const needsMainLeague = nextAction === 'join_main_league_first'
  const canJoinSubleague = nextAction === 'join_subleague'

  async function handleMainLeagueJoin() {
    if (!leagueId) return
    try {
      await joinLeagueMutation.mutateAsync()
      onReload()
    } catch {
      // The mutation message is rendered below.
    }
  }

  async function handleSubleagueJoin() {
    try {
      const result = await joinSubleagueMutation.mutateAsync()
      if (result.status === 'joined' || result.status === 'already_member') {
        if (result.fantasy_league_id ?? leagueId) onOpenLeague(result.fantasy_league_id ?? leagueId!)
        return
      }
      onReload()
    } catch {
      // The mutation message is rendered below.
    }
  }

  return (
    <InviteShell>
      <InviteBackLink onClick={onBack} label={t('Golero Manager')} />
      <div className="flex h-24 w-24 items-center justify-center rounded-[1.5rem] border border-white/[0.08] bg-[#151a1c] text-4xl">
        ⚽
      </div>
      <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-accent-300">{t('Golero Manager')}</p>
      <h1 className="mt-2 text-3xl font-black tracking-tight text-white">
        {preview.subleague?.name ?? preview.league?.name ?? t('Manager-uitnodiging')}
      </h1>
      <p className="mt-3 text-sm leading-6 text-surface-400">
        {t('Je bent uitgenodigd om mee te doen met deze Manager-competitie en je eigen team te beheren.')}
      </p>
      <InviteMeta inviter={preview.inviter?.display_name} />
      {!preview.invite_valid ? (
        <InviteState compact title={inviteReasonTitle(preview.reason)} body={t('Deze uitnodiging kan niet meer worden gebruikt.')} />
      ) : isJoined ? (
        <InviteState
          compact
          title={t('Je doet al mee')}
          body={t('Open de Manager-competitie om je team te bekijken.')}
          action={leagueId ? <button type="button" onClick={() => onOpenLeague(leagueId)} className={primaryButtonClass}>{t('Open Manager-competitie')}</button> : undefined}
        />
      ) : needsMainLeague ? (
        <InviteAction
          label={t('Deelnemen aan Manager-competitie')}
          pending={joinLeagueMutation.isPending}
          error={joinLeagueMutation.error?.message}
          onClick={() => void handleMainLeagueJoin()}
        />
      ) : canJoinSubleague ? (
        <InviteAction
          label={t('Deelnemen aan Manager-poule')}
          pending={joinSubleagueMutation.isPending}
          error={joinSubleagueMutation.error?.message}
          onClick={() => void handleSubleagueJoin()}
        />
      ) : (
        <InviteState compact title={t('Deze invite kan niet worden gebruikt')} body={t('De Manager-competitie is mogelijk gesloten of nog niet beschikbaar.')} />
      )}
    </InviteShell>
  )
}

function InviteShell({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-2xl">
      <section className="overflow-hidden rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] shadow-[0_24px_70px_rgba(0,0,0,0.24)]">
        <div className="p-5 sm:p-8">{children}</div>
      </section>
    </div>
  )
}

function InviteBackLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-surface-500 transition hover:text-surface-200">
      <span aria-hidden="true">←</span>
      {label}
    </button>
  )
}

function InviteHeroImage({ src }: { src?: string | null | undefined }) {
  return src ? (
    <img src={src} alt="" className="h-32 w-full rounded-[1.25rem] object-cover opacity-90" />
  ) : (
    <div className="h-32 w-full rounded-[1.25rem] bg-[radial-gradient(circle_at_20%_20%,rgba(96,165,250,0.18),transparent_38%),#151a1c]" />
  )
}

function InviteMeta({
  inviter,
  members,
  approval,
}: {
  inviter?: string | null | undefined
  members?: number | undefined
  approval?: boolean | undefined
}) {
  const { t } = useTranslation()
  const items = [
    inviter ? t('Uitgenodigd door {name}', { name: inviter }) : null,
    typeof members === 'number' ? t('{count} deelnemers', { count: members }) : null,
    approval ? t('Na goedkeuring') : null,
  ].filter(Boolean)

  return items.length > 0 ? (
    <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 border-y border-white/[0.07] py-4 text-xs text-surface-500">
      {items.map((item) => <span key={item}>{item}</span>)}
    </div>
  ) : null
}

function InviteAction({
  label,
  pending,
  error,
  onClick,
}: {
  label: string
  pending: boolean
  error?: string | undefined
  onClick: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="mt-6">
      <button type="button" onClick={onClick} disabled={pending} className={`${primaryButtonClass} w-full sm:w-auto`}>
        {pending ? t('Bezig…') : label}
      </button>
      {error ? <p className="mt-3 text-sm text-red-300">{error}</p> : null}
    </div>
  )
}

function InviteState({
  eyebrow,
  title,
  body,
  action,
  compact = false,
}: {
  eyebrow?: string
  title: string
  body: string
  action?: ReactNode
  compact?: boolean
}) {
  return (
    <div className={compact ? 'mt-6 border-t border-white/[0.07] pt-5' : ''}>
      {eyebrow ? <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent-300">{eyebrow}</p> : null}
      <h1 className={eyebrow ? 'mt-2 text-2xl font-black tracking-tight text-white' : 'text-xl font-black text-white'}>{title}</h1>
      <p className="mt-2 max-w-xl text-sm leading-6 text-surface-400">{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}

function inviteReasonTitle(reason: string) {
  const t = (key: string) => translate(key)
  switch (reason) {
    case 'expired': return t('Deze invite is verlopen')
    case 'revoked': return t('Deze invite is ingetrokken')
    case 'exhausted': return t('Deze invite is volgebruikt')
    case 'poule_closed':
    case 'league_closed': return t('Deze competitie is gesloten')
    default: return t('Ongeldige invite-link')
  }
}

const primaryButtonClass = 'inline-flex min-h-12 items-center justify-center rounded-xl bg-accent-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-wait disabled:opacity-60'
